/**
 * 远程协助：申请、授权、以及把本机的鼠标键盘动作发给被控方。
 *
 * 对应老系统 WOBJ_APPREQUESTCTRL / APPREQUESTCTRLACK /
 * APPCTRLMOUSE / APPCTRLKEY 一组消息。
 *
 * 安全边界：指令能不能落到对方机器上，由**被控方**的桌面端决定
 * （Rust 侧的 ControlGuard 校验席位），这里的界面只负责表达意图。
 * 网页端因浏览器限制无法被控，但可以去控制别人。
 */

import { useEffect, useState } from 'react'

import { capabilities, isDesktop, isMobileApp } from '../lib/desktop'
import { USER_NONE } from '../lib/protocol'
import { useRoomStore } from '../stores/roomStore'

/** 授权请求提示：只有正在共享的人会看到。 */
export function ControlRequestPrompt() {
  const members = useRoomStore((s) => s.members)
  const from = useRoomStore((s) => s.ctrlRequestFrom)
  const replyControl = useRoomStore((s) => s.replyControl)
  const [hint, setHint] = useState<string | undefined>()
  // 能不能被远程操作由客户端自己报（capabilities.remoteInput）：网页版和手机 App 都不能，
  // 只有桌面 App 可以 —— 不能拿「是不是 Tauri」来判，手机 App 也是 Tauri。
  const [controllable, setControllable] = useState(false)

  // 首次弹出时把系统授权提示查出来 —— macOS 需要「辅助功能」权限，
  // 等到点了没反应再排查会浪费很多时间。
  // 网页端不显示这条：它下面已经有一句更明确的红字说明了，两句话意思重复。
  useEffect(() => {
    if (from === USER_NONE || !isDesktop()) return
    void capabilities().then((c) => {
      setHint(c.permissionHint)
      setControllable(!!c.remoteInput)
    })
  }, [from])

  if (from === USER_NONE) return null
  const name = members.get(from)?.name ?? `席位 ${from}`

  return (
    <div className="dlg-mask">
      <div className="dlg narrow" onClick={(e) => e.stopPropagation()}>
        <h3>远程协助请求</h3>
        <p>
          <strong>{name}</strong> 请求操作你的桌面。同意后，对方的鼠标与键盘动作
          会直接作用在你的电脑上。
        </p>
        {!isDesktop() && (
          <div className="login-err">
            网页版无法被远程操作。如需接受协助，请改用桌面客户端。
          </div>
        )}
        {isMobileApp() && (
          <div className="login-err">手机上无法被远程操作。如需接受协助，请改用电脑上的桌面客户端。</div>
        )}
        {hint && <p className="adm-hint">{hint}</p>}
        <div className="dlg-foot">
          <button onClick={() => replyControl(from, false)}>谢绝</button>
          <button
            className="primary"
            disabled={!controllable}
            onClick={() => replyControl(from, true)}
          >
            允许操作我的桌面
          </button>
        </div>
      </div>
    </div>
  )
}

/** 申请远程协助的按钮：有人共享且不是自己时出现。 */
export function ControlRequestButton() {
  const selfId = useRoomStore((s) => s.selfId)
  const sharerId = useRoomStore((s) => s.sharerId)
  const controllingId = useRoomStore((s) => s.controllingId)
  const requestControl = useRoomStore((s) => s.requestControl)

  if (sharerId === USER_NONE || sharerId === selfId) return null
  if (controllingId !== USER_NONE) {
    return <span className="ctrl-badge">你正在远程操作对方桌面</span>
  }
  return (
    <button className="mini" onClick={requestControl} title="申请操作共享者的桌面">
      申请远程协助
    </button>
  )
}

/**
 * 控制层：盖在共享画面上，把指针与按键动作转成归一化坐标发出去。
 *
 * 坐标必须按**画面实际显示区域**换算，而不是元素尺寸 ——
 * 共享画面是 object-fit: contain，元素里通常有黑边，
 * 用元素尺寸算会让光标整体偏移。
 */
export function ControlOverlay({ videoEl }: { videoEl: HTMLVideoElement | null }) {
  const controllingId = useRoomStore((s) => s.controllingId)
  const sendMouse = useRoomStore((s) => s.sendControlMouse)
  const sendKey = useRoomStore((s) => s.sendControlKey)

  // 键盘事件挂在 window 上：被控期间用户的按键不一定落在覆盖层上。
  useEffect(() => {
    if (controllingId === USER_NONE) return

    const mods = (e: KeyboardEvent) =>
      (e.shiftKey ? 1 : 0) | (e.ctrlKey ? 2 : 0) | (e.altKey ? 4 : 0) | (e.metaKey ? 8 : 0)

    const onDown = (e: KeyboardEvent) => {
      // Esc 用于退出控制，不转发。
      if (e.key === 'Escape') return
      e.preventDefault()
      sendKey({ code: e.code, action: 'down', mods: mods(e) })
    }
    const onUp = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return
      e.preventDefault()
      sendKey({ code: e.code, action: 'up', mods: mods(e) })
    }

    window.addEventListener('keydown', onDown, true)
    window.addEventListener('keyup', onUp, true)
    return () => {
      window.removeEventListener('keydown', onDown, true)
      window.removeEventListener('keyup', onUp, true)
    }
  }, [controllingId, sendKey])

  if (controllingId === USER_NONE) return null

  /** 把指针位置换算成共享画面内的归一化坐标；落在黑边上返回 null。 */
  const toNorm = (e: React.PointerEvent): { x: number; y: number } | null => {
    const el = videoEl
    if (!el || !el.videoWidth || !el.videoHeight) return null
    const r = el.getBoundingClientRect()

    // contain 布局下画面实际占据的区域
    const scale = Math.min(r.width / el.videoWidth, r.height / el.videoHeight)
    const w = el.videoWidth * scale
    const h = el.videoHeight * scale
    const left = r.left + (r.width - w) / 2
    const top = r.top + (r.height - h) / 2

    const x = (e.clientX - left) / w
    const y = (e.clientY - top) / h
    if (x < 0 || x > 1 || y < 0 || y > 1) return null
    return { x, y }
  }

  return (
    <div
      className="ctrl-overlay"
      title="正在远程操作对方桌面，按 Esc 可交还控制"
      onPointerMove={(e) => {
        const p = toNorm(e)
        if (p) sendMouse({ ...p, button: 0, action: 'move' })
      }}
      onPointerDown={(e) => {
        const p = toNorm(e)
        if (p) sendMouse({ ...p, button: e.button, action: 'down' })
      }}
      onPointerUp={(e) => {
        const p = toNorm(e)
        if (p) sendMouse({ ...p, button: e.button, action: 'up' })
      }}
      onContextMenu={(e) => e.preventDefault()}
      onWheel={(e) => {
        const p = toNorm(e as unknown as React.PointerEvent)
        if (p) sendMouse({ ...p, button: 0, action: 'wheel', delta: e.deltaY > 0 ? -1 : 1 })
      }}
    >
      <span className="ctrl-hint">远程操作中 · 按 Esc 交还控制</span>
    </div>
  )
}
